import { useState, useCallback, useEffect, createContext, useContext } from 'react';
import { authService, type LockoutStatus } from '@/services/auth.service';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  status: AuthStatus;
  username: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  lockout: LockoutStatus | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  checkLockout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [username, setUsername] = useState<string | null>(null);
  const [lockout, setLockout] = useState<LockoutStatus | null>(null);

  const checkAuth = useCallback(async () => {
    try {
      const me = await authService.me();
      setUsername(me.username);
      setStatus('authenticated');
    } catch {
      setUsername(null);
      setStatus('unauthenticated');
    }
  }, []);

  const checkLockout = useCallback(async () => {
    try {
      const data = await authService.lockoutStatus();
      setLockout(data);
    } catch {
      setLockout(null);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(
    async (usernameInput: string, passwordInput: string) => {
      await authService.login(usernameInput, passwordInput);
      setUsername(usernameInput);
      setStatus('authenticated');
      try {
        await checkLockout();
      } catch {
        // Non-fatal: lockout status refresh is best-effort.
      }
    },
    [checkLockout]
  );

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch {
      // Ignore logout errors; we're clearing local state regardless.
    }
    setUsername(null);
    setStatus('unauthenticated');
  }, []);

  return (
    <AuthContext.Provider
      value={{
        status,
        username,
        isAuthenticated: status === 'authenticated',
        isLoading: status === 'loading',
        lockout,
        login,
        logout,
        checkAuth,
        checkLockout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = (): AuthState => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};

export default useAuth;
